// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { ShareOptions } from "@insite/client-framework/Services/WishListService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import WishListSharingStatus, {
    WishListSharingStatusStyles,
} from "@insite/content-library/Components/WishListSharingStatus";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import { IconPresentationProps } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import OverflowMenu, { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import React, { useContext } from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

/* Valtech Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { tableRemoveEllipsisCellProps } from "@store/Themes/common/props/table";
import { wishListTableProps } from "@store/Themes/common/props/wishList";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import useMergeProps from "@toolkit/Framework/Common/Hooks/useMergeProps";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
/* Valtech Custom Code - End */

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    allowEditingOfWishLists: getSettingsCollection(state).wishListSettings.allowEditingOfWishLists,
    isUpdatingCart: state.context.isUpdatingCart,
});

interface OwnProps {
    wishList: WishListModel;
    extendedStyles?: WishListCardStyles;
    addWishListToCart: HandleThunkActionCreator<typeof addWishListToCart>;
    deleteWishList?: () => void;
    leaveWishList?: () => void;
}

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface WishListCardStyles {
    gridContainer?: GridContainerProps;
    wishListInfoGridItem?: GridItemProps;
    wishListInfoGridContainer?: GridContainerProps;
    basicInfoGridItem?: GridItemProps;
    basicInfoGridContainer?: GridContainerProps;
    detailLinkGridItem?: GridItemProps;
    descriptionGridItem?: GridItemProps;
    descriptionText?: TypographyProps;
    lastUpdatedGridItem?: GridItemProps;
    lastUpdatedText?: TypographyProps;
    extendedInfoGridItem?: GridItemProps;
    extendedInfoGridContainer?: GridContainerProps;
    productImagesGridItem?: GridItemProps;
    productImageLink?: LinkPresentationProps;
    productImage?: LazyImageProps;
    sharingStatusGridItem?: GridItemProps;
    sharingStatus?: WishListSharingStatusStyles;
    actionGridItem?: GridItemProps;
    actionGridMediumHidden?: HiddenProps;
    actionOverflowIcon?: IconPresentationProps;
    actionOverflowMenu?: OverflowMenuPresentationProps;
    actionAddToCartClickable?: ClickablePresentationProps;
    actionDeleteClickable?: ClickablePresentationProps;
    actionLeaveClickable?: ClickablePresentationProps;
    actionAddToCartButton?: ButtonPresentationProps;
    actionDeleteButton?: ButtonPresentationProps;
    actionLeaveButton?: ButtonPresentationProps;
    // Valtech Custom Code
    listNameDescriptionCell?: DataTableCellProps;
    dateCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    itemsCell?: DataTableCellProps;
    itemsWrapper?: InjectableCss;
    itemsImageWrapper?: InjectableCss;
    itemProductImage?: ProductImageStyles;
    actionsCell?: DataTableCellProps;
    actionsWrapper?: InjectableCss;
    actionAddToCartLink?: LinkPresentationProps;
    actionLeaveLink?: LinkPresentationProps;
    actionDeleteLink?: LinkPresentationProps;
}

export const wishListCardStyles: WishListCardStyles = {
    listNameDescriptionCell: wishListTableProps.listNameDescriptionCell,
    descriptionText: wishListTableProps.listDescriptionText,
    dateCell: wishListTableProps.dateCell,
    lastUpdatedText: wishListTableProps.lastUpdatedText,
    itemsCell: wishListTableProps.itemsCell,
    itemsWrapper: wishListTableProps.itemsWrapper,
    itemsImageWrapper: wishListTableProps.itemsImageWrapper,
    itemProductImage: wishListTableProps.itemProductImage,
    statusCell: wishListTableProps.statusCell,
    sharingStatus: wishListTableProps.sharingStatus,
    actionsCell: wishListTableProps.actionsCell,
    actionsWrapper: wishListTableProps.actionsWrapper,
    actionAddToCartLink: wishListTableProps.actionAddToCartLink,
    actionLeaveLink: wishListTableProps.actionLeaveLink,
    actionDeleteLink: wishListTableProps.actionDeleteLink,
};

const WishListCard = ({
    language,
    allowEditingOfWishLists,
    isUpdatingCart,
    extendedStyles,
    wishList,
    addWishListToCart,
    deleteWishList,
    leaveWishList,
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    /* Valtech Custom Code - Start */
    const trackAddWishListToCart = ga.use.trackAddWishListToCart({}, { wishList });
    const trackAddToCart = ga.use.trackAddToCart({}, { wishList });
    /* Valtech Custom Code - End */

    const clickAddToCartHandler = (e: any) => {
        e.preventDefault();
        addWishListToCart({
            apiParameter: { wishListId: wishList.id },
            onSuccess: onAddAllToCartSuccess,
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    };

    const onAddAllToCartSuccess = (result: CartLineCollectionModel) => {
        /* Valtech Custom Code - Start */
        trackAddWishListToCart();
        trackAddToCart();
        /* Valtech Custom Code - End */
        toasterContext.addToast({
            body: result.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : translate("List Added to Cart"),
            messageType: "success",
        });
    };

    const updatedOnDisplay = getLocalizedDateTime({
        dateTime: new Date(wishList.updatedOn),
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });
    /* Valtech Custom Code - Start */
    const lastUpdatedDisplay =
        wishList.updatedByDisplayName && !wishList.isAutogenerated
            ? ` ${translate("by")} ${wishList.updatedByDisplayName}`
            : null;
    /* Valtech Custom Code - End */

    const canAddToCart = wishList.canAddToCart && !!wishList.wishListLinesCount && wishList.wishListLinesCount > 0;
    const addToCartLabel = translate("Add List to Cart");
    const deleteLabel = translate("Delete");
    const leaveLabel = translate("Leave List");
    const styles = useMergeProps(wishListCardStyles, extendedStyles); // Valtech Custom Code

    /* Valtech Custom Code - Start */
    const statusCellStyles = React.useMemo(() => {
        if (wishList?.isSharedList || wishList?.wishListSharesCount) {
            return styles.statusCell;
        }

        return mergeProps<typeof styles.statusCell>(styles.statusCell, tableRemoveEllipsisCellProps());
    }, [styles.statusCell, wishList?.isSharedList, wishList?.wishListSharesCount]);
    /* Valtech Custom Code - End */

    return (
        <>
            {/* Valtech Custom Code - Start */}
            <DataTableRow data-test-selector="wishListCard">
                <DataTableCell {...styles.listNameDescriptionCell}>
                    <MyListsDetailsPageTypeLink
                        title={wishList.name}
                        wishListId={wishList.id}
                        testSelector="wishListCardName"
                    />
                    {wishList.description && (
                        <Typography {...styles.descriptionText} data-test-selector="wishListCardDescription">
                            {wishList.description}
                        </Typography>
                    )}
                </DataTableCell>
                <DataTableCell {...styles.dateCell}>
                    <Typography {...styles.lastUpdatedText} data-test-selector="wishListCardLastUpdated">
                        {updatedOnDisplay}
                    </Typography>
                    {lastUpdatedDisplay ? (
                        <Typography {...styles.lastUpdatedText} data-test-selector="wishListCardLastUpdated">
                            {lastUpdatedDisplay}
                        </Typography>
                    ) : null}
                </DataTableCell>
                <DataTableCell {...styles.itemsCell}>
                    <StyledWrapper {...styles.itemsWrapper}>
                        {wishList.wishListLineCollection &&
                            wishList.wishListLineCollection.length > 0 &&
                            wishList.wishListLineCollection.slice(0, 3).map(line => (
                                <StyledWrapper {...styles.itemsImageWrapper} key={line.id.toString()}>
                                    <ProductImage product={line as any} extendedStyles={styles.itemProductImage} />
                                </StyledWrapper>
                            ))}
                    </StyledWrapper>
                </DataTableCell>
                <DataTableCell {...statusCellStyles}>
                    <WishListSharingStatus extendedStyles={styles.sharingStatus} wishList={wishList} />
                </DataTableCell>
                <DataTableCell {...styles.actionsCell}>
                    <StyledWrapper {...styles.actionsWrapper}>
                        <Link
                            {...styles.actionAddToCartLink}
                            disabled={!canAddToCart || isUpdatingCart}
                            onClick={clickAddToCartHandler}
                        >
                            {addToCartLabel}
                        </Link>
                        {allowEditingOfWishLists &&
                        !!leaveWishList &&
                        wishList.isSharedList &&
                        wishList.shareOption !== ShareOptions.AllCustomerUsers ? (
                            <Link {...styles.actionLeaveLink} onClick={leaveWishList}>
                                {leaveLabel}
                            </Link>
                        ) : (
                            <Link
                                {...styles.actionDeleteLink}
                                disabled={
                                    !deleteWishList ||
                                    wishList.isSharedList ||
                                    !allowEditingOfWishLists ||
                                    wishList.isAutogenerated
                                }
                                onClick={deleteWishList}
                                data-test-selector={`wishListCardDeleteButton_${wishList.id}`}
                            >
                                {deleteLabel}
                            </Link>
                        )}
                    </StyledWrapper>
                </DataTableCell>
            </DataTableRow>
            {/* Valtech Custom Code - End */}
        </>
    );
};

export default connect(mapStateToProps)(WishListCard);
